/* eslint-disable no-console */
import type { AdultProfile, ChildProfile } from '@yoyoclub/shared';
import { DEFAULT_TIMEZONE, enFirst, participantViolation } from '@yoyoclub/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import ConsentSection from '../components/booking/ConsentSection';
import EmptyProfilesState from '../components/booking/EmptyProfilesState';
import ProfileSelector from '../components/booking/ProfileSelector';
import RefundPolicyModal from '../components/booking/RefundPolicyModal';
import SelectedProfileCard from '../components/booking/SelectedProfileCard';
import TemporaryParticipantForm from '../components/booking/TemporaryParticipantForm';
import AdultProfileFormModal from '../components/profiles/AdultProfileFormModal';
import ChildProfileFormModal from '../components/profiles/ChildProfileFormModal';
import { useAuth } from '../hooks/useAuth';
import { dateLocale } from '../i18n/locale';
import { localize } from '../i18n/script';
import { bookingApi } from '../services/bookingApi';
import { consentApi, type PhotoVideoConsent, type UserConsents } from '../services/consentApi';
import { passApi, type AvailablePassForSession } from '../services/passApi';
import { adultProfileApi, childProfileApi } from '../services/profileApi';
import { promotionCodeApi } from '../services/promotionCodeApi';
import { storeCreditApi } from '../services/storeCreditApi';
import { getLocalizedTitle } from '../utils/activityTransform';
import { trackBeginCheckout } from '../utils/analytics';
import { forgetMemberCode, getRememberedMemberCode, promoFailureText } from '../utils/memberCode';
import { getStoredUtmSource } from '../utils/utmTracking';

interface Activity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  price: number;
  adultPrice: number;
  childRequired: boolean;
  adultRequired: boolean;
  childCountsTowardCapacity: boolean;
  adultCountsTowardCapacity: boolean;
  ageMin: number;
  ageMax: number;
}

interface Session {
  id: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  currentBookings: number;
  price?: number | null; // Session child price in cents (null = use activity default)
  adultPrice?: number | null; // Session adult price in cents (null = use activity default)
  descriptionCN?: string | null;
  descriptionEN?: string | null;
}

interface KidParticipant {
  name: string;
  preferredName: string;
  birthYear: string;
  birthMonth: string;
  notes?: string;
}

interface AdultParticipant {
  name: string;
  phone: string;
}

interface GuestInfo {
  email: string;
  name: string;
}

// Participant data for mixed mode (profile or temporary)
interface ParticipantData {
  // Profile mode
  profileId?: string;
  profileType?: 'child' | 'adult';
  profile?: ChildProfile | AdultProfile;

  // Temporary mode
  isTemporary?: boolean;
  tempData?: {
    name: string;
    preferredName?: string; // For child
    birthYear?: string; // For child
    birthMonth?: string; // For child
    notes?: string; // For child
    phone?: string; // For adult
  };
}

export default function BookingCheckout() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['booking', 'common', 'pass']);
  const { user, isAuthenticated, getToken } = useAuth();

  const [activity, setActivity] = useState<Activity | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({
    email: user?.email || '',
    name: user?.name || '',
  });
  const [emailWarning, setEmailWarning] = useState<{
    message: string;
    suggestion?: string;
  } | null>(null);
  const [emailValidating, setEmailValidating] = useState(false);
  const emailValidationRef = useRef(0);

  const validateGuestEmail = async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailWarning(null);
      return;
    }
    const requestId = ++emailValidationRef.current;
    setEmailValidating(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/payments/validate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const result = await response.json();
      // Ignore stale results if user has already changed the email
      if (requestId !== emailValidationRef.current) return;
      if (result.success && result.data && !result.data.valid) {
        const { errorCode, suggestion } = result.data;
        if (errorCode === 'DOMAIN_TYPO' && suggestion) {
          setEmailWarning({
            message: t('booking:checkout.contactInfo.emailTypo', { suggestion }),
            suggestion,
          });
        } else if (errorCode === 'DOMAIN_NO_MX') {
          setEmailWarning({
            message: t('booking:checkout.contactInfo.emailInvalidDomain'),
          });
        } else {
          setEmailWarning(null);
        }
      } else {
        setEmailWarning(null);
      }
    } catch {
      if (requestId !== emailValidationRef.current) return;
      setEmailWarning(null);
    } finally {
      if (requestId === emailValidationRef.current) setEmailValidating(false);
    }
  };

  const [kids, setKids] = useState<KidParticipant[]>([
    { name: '', preferredName: '', birthYear: '', birthMonth: '', notes: '' },
  ]);

  const [adults, setAdults] = useState<AdultParticipant[]>([{ name: '', phone: '' }]);

  // Profile-based state (for authenticated users)
  const [childProfiles, setChildProfiles] = useState<ChildProfile[]>([]);
  const [adultProfiles, setAdultProfiles] = useState<AdultProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);

  // Selected participants (mixed mode: profiles + temporary)
  const [selectedChildren, setSelectedChildren] = useState<ParticipantData[]>([]);
  const [selectedAdults, setSelectedAdults] = useState<ParticipantData[]>([]);

  // UI state
  const [showChildProfileSelector, setShowChildProfileSelector] = useState(false);
  const [showAdultProfileSelector, setShowAdultProfileSelector] = useState(false);
  const [showChildTempForm, setShowChildTempForm] = useState(false);
  const [showAdultTempForm, setShowAdultTempForm] = useState(false);
  const [showCreateChildModal, setShowCreateChildModal] = useState(false);
  const [showCreateAdultModal, setShowCreateAdultModal] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState<string | null>(null);

  // Store credit state
  const [creditBalance, setCreditBalance] = useState<number>(0); // in cents

  // Consent state
  const [photoVideoConsent, setPhotoVideoConsent] = useState<PhotoVideoConsent | null>(null);
  const [waiverAgreed, setWaiverAgreed] = useState(false);
  const [releaseAgreed, setReleaseAgreed] = useState(false);
  const [hasExistingConsent, setHasExistingConsent] = useState(false); // True if user already signed all consents
  const [refundPolicyAgreed, setRefundPolicyAgreed] = useState(false);
  const [showRefundPolicyModal, setShowRefundPolicyModal] = useState(false);

  // Pass booking state
  const [availablePasses, setAvailablePasses] = useState<AvailablePassForSession[]>([]);
  const [usePassMode, setUsePassMode] = useState(false);
  const [selectedPassId, setSelectedPassId] = useState<string | null>(null);
  const [passBookingSubmitting, setPassBookingSubmitting] = useState(false);

  // Promotion code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<{
    promotionCodeId: string;
    code: string;
    discountType: 'FIXED_AMOUNT' | 'PERCENTAGE';
    discountValue: number;
  } | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  // A member's new user promo code, stacked on at most one platform code (member rewards PRD §3.2)
  const [appliedInvite, setAppliedInvite] = useState<{
    promotionCodeId: string;
    code: string;
    discountValue: number;
  } | null>(null);
  const promoValidationRef = useRef(0);

  // Fetch activity and session details
  useEffect(() => {
    const fetchData = async () => {
      if (!slug || !sessionId) {
        setError('Missing activity or session information');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

        // Fetch activity details
        const activityResponse = await fetch(`${apiUrl}/activities/slug/${slug}`);
        if (!activityResponse.ok) throw new Error('Failed to fetch activity');

        const activityResult = await activityResponse.json();
        if (!activityResult.success || !activityResult.data) {
          throw new Error('Activity not found');
        }

        const activityData = activityResult.data;
        setActivity({
          id: activityData.id,
          titleCN: activityData.titleCN,
          titleEN: activityData.titleEN,
          price: activityData.price,
          adultPrice: activityData.adultPrice ?? 0,
          childRequired: activityData.childRequired ?? true,
          adultRequired: activityData.adultRequired ?? true,
          childCountsTowardCapacity: activityData.childCountsTowardCapacity ?? true,
          adultCountsTowardCapacity: activityData.adultCountsTowardCapacity ?? false,
          ageMin: activityData.ageMin,
          ageMax: activityData.ageMax,
        });

        // Find the specific session
        const sessionData = activityData.sessions.find((s: { id: string }) => s.id === sessionId);
        if (!sessionData) {
          throw new Error('Session not found');
        }

        setSession(sessionData);

        // GA4: track begin_checkout (use session price override if available)
        const effectivePrice = sessionData.price ?? activityData.price;
        trackBeginCheckout({
          type: 'booking',
          title: enFirst(activityData.titleCN, activityData.titleEN),
          value: effectivePrice / 100,
        });
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load booking information';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, sessionId]);

  // Clear kids for adult-only activities
  useEffect(() => {
    if (activity && !activity.childRequired) {
      setKids([]);
      setSelectedChildren([]);
    }
  }, [activity]);

  // Kid-only activities: adults are optional, so start guests with no adult rows
  useEffect(() => {
    if (activity && !activity.adultRequired) {
      setAdults((prev) => (prev.length === 1 && !prev[0].name && !prev[0].phone ? [] : prev));
    }
  }, [activity]);

  // Pre-fill user info if logged in
  useEffect(() => {
    if (user) {
      setGuestInfo((prev) => ({
        ...prev,
        email: user.email || '',
        name: user.name || '',
      }));
    }
  }, [user]);

  // Load profiles for authenticated users
  useEffect(() => {
    const loadProfiles = async () => {
      if (!isAuthenticated) return;

      try {
        setProfilesLoading(true);
        setProfileLoadError(null);
        const [children, adults] = await Promise.all([
          childProfileApi.getUserProfiles(),
          adultProfileApi.getUserProfiles(),
        ]);
        setChildProfiles(children);
        setAdultProfiles(adults);
      } catch (err) {
        console.error('Failed to load profiles:', err);
        setProfileLoadError(t('booking:consent.errors.profileLoadError'));
      } finally {
        setProfilesLoading(false);
      }
    };

    loadProfiles();
  }, [isAuthenticated, t]);

  // Load store credit balance for authenticated users
  useEffect(() => {
    const loadCreditBalance = async () => {
      if (!isAuthenticated) return;
      try {
        const token = await getToken();
        if (token) {
          const balance = await storeCreditApi.getBalance(token);
          setCreditBalance(balance);
        }
      } catch (err) {
        console.error('Failed to load store credit balance:', err);
      }
    };

    loadCreditBalance();
  }, [isAuthenticated, getToken]);

  // Fetch available passes for this session.
  // Skip entirely when arriving from a waitlist claim — PRD §1.4 says the
  // waitlist feature only covers regular session booking, and the pass path
  // would not honor waitlistId cleanup (PRD §5.5 / §7.2).
  const isWaitlistClaim = !!searchParams.get('waitlistId');
  useEffect(() => {
    const fetchAvailablePasses = async () => {
      if (!isAuthenticated || !session || isWaitlistClaim) return;

      try {
        const token = await getToken();
        if (!token) return;
        const passes = await passApi.getAvailablePassesForSession(session.id, token);
        setAvailablePasses(passes);
        // Auto-select if only one pass available
        if (passes.length === 1) {
          setSelectedPassId(passes[0].id);
        }
      } catch {
        // Non-critical — user can still pay normally
      }
    };

    fetchAvailablePasses();
  }, [isAuthenticated, session, getToken, isWaitlistClaim]);

  // Stored consent data from API (loaded once, evaluated when activity is known)
  const [loadedConsents, setLoadedConsents] = useState<UserConsents | null>(null);

  // Load user's consent status (authenticated users only)
  useEffect(() => {
    const loadUserConsents = async () => {
      if (!isAuthenticated) return;

      try {
        const consents = await consentApi.getUserConsents();
        if (consents) {
          setLoadedConsents(consents);
          if (consents.photoVideoConsent) {
            setPhotoVideoConsent(consents.photoVideoConsent);
          }
        }
      } catch (err) {
        console.error('Failed to load user consents:', err);
      }
    };

    loadUserConsents();
  }, [isAuthenticated]);

  // Evaluate hasExistingConsent when both consents and activity are loaded
  useEffect(() => {
    if (!activity) return;
    if (!loadedConsents) {
      setHasExistingConsent(false);
      return;
    }

    const isAdultOnlyActivity = !activity.childRequired;
    const hasPhoto = !!loadedConsents.photoVideoConsent;
    const hasWaiver = !!loadedConsents.liabilityWaiverAgreed;
    const hasRelease = !!loadedConsents.parentalReleaseAgreed;

    // Adult-only: only photo + waiver needed; regular: all three
    const allConsentsSigned = isAdultOnlyActivity
      ? hasPhoto && hasWaiver
      : hasPhoto && hasWaiver && hasRelease;

    setHasExistingConsent(allConsentsSigned);
    setWaiverAgreed(hasWaiver);
    setReleaseAgreed(hasRelease);
  }, [loadedConsents, activity]);

  const handleAddKid = () => {
    setKids([...kids, { name: '', preferredName: '', birthYear: '', birthMonth: '', notes: '' }]);
  };

  const handleRemoveKid = (index: number) => {
    setKids(kids.filter((_, i) => i !== index));
  };

  const handleKidChange = (index: number, field: keyof KidParticipant, value: string) => {
    const newKids = [...kids];
    newKids[index] = { ...newKids[index], [field]: value };
    setKids(newKids);
  };

  const handleAddAdult = () => {
    setAdults([...adults, { name: '', phone: '' }]);
  };

  const handleAdultChange = (index: number, field: keyof AdultParticipant, value: string) => {
    const newAdults = [...adults];
    newAdults[index] = { ...newAdults[index], [field]: value };
    setAdults(newAdults);
  };

  // Profile-based handlers
  const handleSelectChildProfiles = (profileIds: string[]) => {
    const newChildren = profileIds.map((id) => {
      const profile = childProfiles.find((p) => p.id === id);
      return {
        profileId: id,
        profileType: 'child' as const,
        profile,
      };
    });

    setSelectedChildren([...selectedChildren, ...newChildren]);
    setShowChildProfileSelector(false);
  };

  const handleSelectAdultProfiles = (profileIds: string[]) => {
    const newAdults = profileIds.map((id) => {
      const profile = adultProfiles.find((p) => p.id === id);
      return {
        profileId: id,
        profileType: 'adult' as const,
        profile,
      };
    });

    setSelectedAdults([...selectedAdults, ...newAdults]);
    setShowAdultProfileSelector(false);
  };

  const handleAddTempChild = async (data: {
    name: string;
    preferredName?: string;
    birthYear?: string;
    birthMonth?: string;
    notes?: string;
    saveAsProfile?: boolean;
  }) => {
    // If user wants to save as profile, create it first
    if (data.saveAsProfile && data.birthYear && data.birthMonth) {
      try {
        // Convert year/month to ISO date string (first day of the month)
        const birthDate = new Date(
          parseInt(data.birthYear),
          parseInt(data.birthMonth) - 1,
          1
        ).toISOString();

        const newProfile = await childProfileApi.createProfile({
          name: data.name,
          preferredName: data.preferredName || data.name,
          birthDate: birthDate,
          notes: data.notes || undefined,
        });

        // Reload profiles and add the new profile to selected
        const children = await childProfileApi.getUserProfiles();
        setChildProfiles(children);

        setSelectedChildren([
          ...selectedChildren,
          {
            profileId: newProfile.id,
            profileType: 'child',
            profile: newProfile,
          },
        ]);

        // Show success message
        setSuccessMessage(`Profile "${data.name}" created and added!`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        console.error('Failed to create child profile:', err);
        // Fallback to temporary mode if creation fails
        setError('Failed to save profile. Adding as temporary participant instead.');
        setTimeout(() => setError(null), 5000);

        setSelectedChildren([
          ...selectedChildren,
          {
            isTemporary: true,
            tempData: data,
          },
        ]);
      }
    } else {
      // Add as temporary participant
      setSelectedChildren([
        ...selectedChildren,
        {
          isTemporary: true,
          tempData: data,
        },
      ]);
    }
    setShowChildTempForm(false);
  };

  const handleAddTempAdult = async (data: {
    name: string;
    phone?: string;
    saveAsProfile?: boolean;
  }) => {
    // If user wants to save as profile, create it first
    if (data.saveAsProfile && data.phone) {
      try {
        const newProfile = await adultProfileApi.createProfile({
          name: data.name,
          phone: data.phone,
        });

        // Reload profiles and add the new profile to selected
        const adults = await adultProfileApi.getUserProfiles();
        setAdultProfiles(adults);

        setSelectedAdults([
          ...selectedAdults,
          {
            profileId: newProfile.id,
            profileType: 'adult',
            profile: newProfile,
          },
        ]);

        // Show success message
        setSuccessMessage(`Profile "${data.name}" created and added!`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        console.error('Failed to create adult profile:', err);
        // Fallback to temporary mode if creation fails
        setError('Failed to save profile. Adding as temporary participant instead.');
        setTimeout(() => setError(null), 5000);

        setSelectedAdults([
          ...selectedAdults,
          {
            isTemporary: true,
            tempData: data,
          },
        ]);
      }
    } else {
      // Add as temporary participant
      setSelectedAdults([
        ...selectedAdults,
        {
          isTemporary: true,
          tempData: data,
        },
      ]);
    }
    setShowAdultTempForm(false);
  };

  const handleRemoveChild = (index: number) => {
    setSelectedChildren(selectedChildren.filter((_, idx) => idx !== index));
  };

  const handleRemoveAdult = (index: number) => {
    setSelectedAdults(selectedAdults.filter((_, idx) => idx !== index));
  };

  const handleCreateChildProfileClose = async () => {
    setShowCreateChildModal(false);

    // Reload profiles to get the newly created one
    try {
      const children = await childProfileApi.getUserProfiles();
      setChildProfiles(children);

      // Auto-select the newly created profile (last one in the list)
      if (children.length > childProfiles.length) {
        const newProfile = children[children.length - 1];
        setSelectedChildren([
          ...selectedChildren,
          {
            profileId: newProfile.id,
            profileType: 'child',
            profile: newProfile,
          },
        ]);
      }
    } catch (err) {
      console.error('Failed to reload child profiles:', err);
    }
  };

  const handleCreateAdultProfileClose = async () => {
    setShowCreateAdultModal(false);

    // Reload profiles to get the newly created one
    try {
      const adults = await adultProfileApi.getUserProfiles();
      setAdultProfiles(adults);

      // Auto-select the newly created profile (last one in the list)
      if (adults.length > adultProfiles.length) {
        const newProfile = adults[adults.length - 1];
        setSelectedAdults([
          ...selectedAdults,
          {
            profileId: newProfile.id,
            profileType: 'adult',
            profile: newProfile,
          },
        ]);
      }
    } catch (err) {
      console.error('Failed to reload adult profiles:', err);
    }
  };

  const handlePassBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!activity || !session || !selectedPassId) {
      setError('Missing required information');
      return;
    }

    // Validate participants
    const childParticipants = selectedChildren.length;
    const adultParticipants = selectedAdults.length;
    const isAdultOnlyActivity = !activity.childRequired;

    const violation = participantViolation(activity, childParticipants, adultParticipants);
    if (violation) {
      setError(
        violation === 'CHILD_REQUIRED'
          ? t('booking:checkout.validation.childRequired')
          : t('booking:checkout.validation.adultRequired')
      );
      return;
    }

    const hasTemporaryParticipants =
      selectedChildren.some((child) => child.isTemporary || !child.profileId) ||
      selectedAdults.some((adult) => adult.isTemporary || !adult.profileId);
    if (hasTemporaryParticipants) {
      setError(t('pass:booking.profilesOnlyError'));
      return;
    }

    // Validate consent forms
    if (!photoVideoConsent) {
      setError(t('booking:consent.validation.photoRequired'));
      return;
    }
    if (!waiverAgreed) {
      setError(t('booking:consent.validation.waiverRequired'));
      return;
    }
    if (!isAdultOnlyActivity && !releaseAgreed) {
      setError(t('booking:consent.validation.releaseRequired'));
      return;
    }

    try {
      setPassBookingSubmitting(true);
      setError(null);
      const token = await getToken();
      if (!token) throw new Error('No access token');

      // Build participants data (Pass booking requires saved profiles only)
      const kidsData = selectedChildren.map((child) => {
        const profileId = child.profileId;
        const profile =
          (child.profile as ChildProfile | undefined) ||
          childProfiles.find((item) => item.id === profileId);
        if (!profileId || !profile) {
          throw new Error(t('pass:booking.profilesOnlyError'));
        }
        return {
          profileId,
          name: profile.preferredName,
          birthDate: new Date(profile.birthDate).toISOString().split('T')[0],
          notes: profile.notes || undefined,
        };
      });

      const adultsData = selectedAdults.map((adult) => {
        const profileId = adult.profileId;
        const profile =
          (adult.profile as AdultProfile | undefined) ||
          adultProfiles.find((item) => item.id === profileId);
        if (!profileId || !profile) {
          throw new Error(t('pass:booking.profilesOnlyError'));
        }
        return {
          profileId,
          name: profile.name,
          phone: profile.phone,
        };
      });

      const result = await passApi.bookWithPass(
        session.id,
        selectedPassId,
        { kids: kidsData, adults: adultsData },
        {
          photoVideoConsent: photoVideoConsent as 'FULL' | 'BLURRED' | 'NO_PHOTO',
          waiverAgreed,
          releaseAgreed,
        },
        token,
        searchParams.get('tc')
      );

      navigate(`/booking-success?bookingId=${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to book with pass');
      setPassBookingSubmitting(false);
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim() || !session) return;
    const requestId = ++promoValidationRef.current;
    setPromoValidating(true);
    setPromoError(null);
    try {
      // Guests need a valid email filled; authenticated users use their account email.
      let email: string;
      let token: string | undefined;
      if (isAuthenticated) {
        token = (await getToken()) || undefined;
        email = user?.email || '';
      } else {
        email = guestInfo.email.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          setPromoError(t('booking:promotionCode.emailRequired'));
          return;
        }
      }
      if (!email) {
        setPromoError(t('booking:promotionCode.emailRequired'));
        return;
      }
      const result = await promotionCodeApi.validate(promoCode.trim(), session.id, email, token);
      // Drop stale responses (e.g. user changed email while the request was in flight).
      if (requestId !== promoValidationRef.current) return;
      if (result.valid) {
        const code = promoCode.trim().toUpperCase();
        // One platform code plus one new user promo code; a second of the same kind replaces the first.
        if (result.isMemberCode) {
          setAppliedInvite({
            promotionCodeId: result.promotionCodeId!,
            code,
            discountValue: result.discountValue!,
          });
        } else {
          setAppliedPromo({
            promotionCodeId: result.promotionCodeId!,
            code,
            discountType: result.discountType!,
            discountValue: result.discountValue!,
          });
        }
        setPromoCode('');
        setPromoError(null);
      } else {
        setPromoError(promoFailureText(t, result.reason, result.message));
      }
    } catch (err) {
      if (requestId !== promoValidationRef.current) return;
      setPromoError(err instanceof Error ? err.message : 'Failed to validate code');
    } finally {
      if (requestId === promoValidationRef.current) setPromoValidating(false);
    }
  };

  // A member's promo code from a shared link (member rewards PRD §5.2): filled in
  // and applied when it works for this buyer, dropped silently when it does not.
  const memberCodeTriedRef = useRef<string | null>(null);
  const tryRememberedMemberCode = useCallback(
    async (email: string) => {
      const remembered = getRememberedMemberCode();
      if (!remembered || !session || usePassMode || appliedInvite || promoCode.trim()) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
      const key = `${remembered}|${email}`;
      if (memberCodeTriedRef.current === key) return;
      memberCodeTriedRef.current = key;
      const requestId = ++promoValidationRef.current;
      try {
        const token = isAuthenticated ? (await getToken()) || undefined : undefined;
        const result = await promotionCodeApi.validate(remembered, session.id, email, token);
        if (requestId !== promoValidationRef.current) return;
        // Only a member's code is applied on its own; a link carrying any other
        // code is not an invitation (member rewards PRD §5.2).
        if (result.valid && result.isMemberCode) {
          setAppliedInvite({
            promotionCodeId: result.promotionCodeId!,
            code: remembered,
            discountValue: result.discountValue!,
          });
          setPromoError(null);
        } else if (isAuthenticated || result.valid) {
          // Not a member code, or it will not work for this account on any order.
          forgetMemberCode();
        }
      } catch {
        // Silent: the buyer can still type a code.
      }
    },
    [session, usePassMode, appliedInvite, promoCode, isAuthenticated, getToken]
  );

  // Signed-in buyers are tried as soon as the order is open; guests once they
  // have entered their email (see the email field's blur handler).
  useEffect(() => {
    if (isAuthenticated && user?.email) void tryRememberedMemberCode(user.email);
  }, [isAuthenticated, user?.email, tryRememberedMemberCode]);

  const handleRemovePromoCode = () => {
    // Bump the ref so any pending validate() response is discarded.
    promoValidationRef.current++;
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError(null);
    setPromoValidating(false);
  };

  const handleRemoveInviteCode = () => {
    promoValidationRef.current++;
    setAppliedInvite(null);
    setPromoError(null);
    setPromoValidating(false);
  };

  /** Both codes go: they were checked against an email or a mode that changed. */
  const clearAllCodes = () => {
    handleRemovePromoCode();
    setAppliedInvite(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!activity || !session) {
      setError('Missing activity or session information');
      return;
    }

    // Determine which participant data to use
    const participantsToUse = isAuthenticated
      ? { children: selectedChildren, adults: selectedAdults }
      : { children: [], adults: [] }; // Guest users use kids/adults arrays

    // Validation - for guests, require email and name
    if (!isAuthenticated && (!guestInfo.email || !guestInfo.name)) {
      setError('Please fill in your email and name');
      return;
    }

    // Block submit if there's an active email warning
    if (!isAuthenticated && emailWarning) {
      setError(t('booking:checkout.contactInfo.emailFixRequired'));
      return;
    }

    // Check participants count
    const childParticipants = isAuthenticated ? selectedChildren.length : kids.length;
    const adultParticipants = isAuthenticated ? selectedAdults.length : adults.length;
    const isAdultOnlyActivity = !activity.childRequired;

    // Adult-only: adults only. Parent-child: a child and an adult. Kid-only: a child,
    // adults optional (shared rule, also enforced by the backend).
    const violation = participantViolation(activity, childParticipants, adultParticipants);
    if (violation) {
      setError(
        violation === 'CHILD_REQUIRED'
          ? t('booking:checkout.validation.childRequired')
          : t('booking:checkout.validation.adultRequired')
      );
      return;
    }

    // Validate guest form
    if (!isAuthenticated) {
      if (!isAdultOnlyActivity) {
        for (const kid of kids) {
          if (!kid.name || !kid.preferredName || !kid.birthYear || !kid.birthMonth) {
            setError('Please fill in all required information for each child');
            return;
          }
        }
      }

      for (const adult of adults) {
        if (!adult.name || !adult.phone) {
          setError('Please fill in all required information for each adult');
          return;
        }
      }
    }

    // Check capacity using explicit strategy fields
    const capacityCount =
      (activity.childCountsTowardCapacity ? childParticipants : 0) +
      (activity.adultCountsTowardCapacity ? adultParticipants : 0);
    if (capacityCount > session.maxCapacity - session.currentBookings) {
      setError(t('booking:checkout.validation.notEnoughSpots'));
      return;
    }

    // Validate consent forms
    if (!photoVideoConsent) {
      setError(t('booking:consent.validation.photoRequired'));
      return;
    }

    if (!waiverAgreed) {
      setError(t('booking:consent.validation.waiverRequired'));
      return;
    }

    if (!isAdultOnlyActivity && !releaseAgreed) {
      setError(t('booking:consent.validation.releaseRequired'));
      return;
    }

    if (!refundPolicyAgreed) {
      setError(t('booking:consent.validation.refundPolicyRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Get auth token if logged in
      const token = isAuthenticated ? await getToken() : undefined;

      // Prepare participants data
      let kidsData: Array<{
        name: string;
        birthDate: string;
        allergies?: string;
        notes?: string;
      }> = [];

      let adultsData: Array<{
        name: string;
        phone: string;
      }> = [];

      if (isAuthenticated) {
        // Convert profile-based participants to API format
        kidsData = participantsToUse.children.map((child) => {
          if (child.isTemporary && child.tempData) {
            // Convert year/month to full birthDate (YYYY-MM-DD format)
            const birthDate =
              child.tempData.birthYear && child.tempData.birthMonth
                ? `${child.tempData.birthYear}-${child.tempData.birthMonth.padStart(2, '0')}-01`
                : '';

            return {
              name: child.tempData.preferredName || child.tempData.name,
              birthDate: birthDate,
              notes: child.tempData.notes,
            };
          } else if (child.profile) {
            const profile = child.profile as ChildProfile;
            return {
              profileId: profile.id, // Add profileId for direct linking
              name: profile.preferredName,
              birthDate: new Date(profile.birthDate).toISOString().split('T')[0],
              notes: profile.notes,
            };
          }
          return { name: '', birthDate: '' };
        });

        adultsData = participantsToUse.adults.map((adult) => {
          if (adult.isTemporary && adult.tempData) {
            return {
              name: adult.tempData.name,
              phone: adult.tempData.phone || '',
            };
          } else if (adult.profile) {
            const profile = adult.profile as AdultProfile;
            return {
              profileId: profile.id, // Add profileId
              name: profile.name,
              phone: profile.phone,
            };
          }
          return { name: '', phone: '' };
        });
      } else {
        // Guest users - convert birthYear/birthMonth to birthDate format
        kidsData = kids.map((kid) => ({
          name: kid.preferredName, // Use preferredName as the display name (same as registered users)
          birthDate: `${kid.birthYear}-${kid.birthMonth.padStart(2, '0')}-01`,
          notes: kid.notes,
        }));
        adultsData = adults;
      }

      // Create checkout session (may return Stripe URL or credit-only booking)
      const result = await bookingApi.createCheckoutSession(
        {
          sessionId: session.id,
          userId: isAuthenticated ? user?.userId || null : null,
          guestInfo: isAuthenticated
            ? null
            : {
                email: guestInfo.email,
                name: guestInfo.name,
              },
          participants: {
            kids: kidsData,
            adults: adultsData,
          },
          photoVideoConsent: photoVideoConsent!,
          utmSource: getStoredUtmSource(),
          transferCode: searchParams.get('tc'),
          locale: i18n.language,
          promotionCodes: [appliedPromo?.code, appliedInvite?.code].filter((c): c is string =>
            Boolean(c)
          ),
          waitlistId: searchParams.get('waitlistId') || undefined,
        },
        token
      );

      if (result.checkoutUrl) {
        // Redirect to Stripe checkout page
        console.log('Checkout session created:', result.checkoutSessionId);
        window.location.href = result.checkoutUrl;
      } else if (result.bookingId) {
        // Credit-only checkout - booking already created, redirect to success page
        navigate(`/booking-success?bookingId=${result.bookingId}`);
      }
    } catch (err: unknown) {
      const errorCode = (err as Error & { code?: string }).code;
      let errorMessage: string;
      if (errorCode === 'DOMAIN_TYPO') {
        // Use the backend's "Did you mean ...?" message which includes the suggestion
        errorMessage =
          err instanceof Error ? err.message : t('booking:checkout.contactInfo.emailInvalidDomain');
      } else if (errorCode === 'DOMAIN_NO_MX') {
        errorMessage = t('booking:checkout.contactInfo.emailInvalidDomain');
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
      }
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('booking:checkout.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !activity) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{error}</h1>
          <button onClick={() => navigate('/')} className="text-primary hover:underline">
            {t('booking:checkout.backButton')}
          </button>
        </div>
      </div>
    );
  }

  if (!activity || !session) {
    return null;
  }

  const childCount = isAuthenticated ? selectedChildren.length : kids.length;
  const adultCount = isAuthenticated ? selectedAdults.length : adults.length;
  const isAdultOnly = !activity.childRequired;

  // Resolve effective prices (session overrides activity)
  const effectiveChildPrice = (session.price ?? activity.price) / 100;
  const effectiveAdultPrice = (session.adultPrice ?? activity.adultPrice) / 100;

  // Unified total price formula
  const totalPrice = effectiveChildPrice * childCount + effectiveAdultPrice * adultCount;
  const totalPriceCents = Math.round(totalPrice * 100);

  // Promotion code discount
  const promoDiscountCents = appliedPromo
    ? appliedPromo.discountType === 'FIXED_AMOUNT'
      ? Math.min(appliedPromo.discountValue, totalPriceCents)
      : Math.floor((totalPriceCents * appliedPromo.discountValue) / 100)
    : 0;
  const inviteDiscountCents = appliedInvite
    ? Math.min(appliedInvite.discountValue, totalPriceCents - promoDiscountCents)
    : 0;
  const postPromoAmountCents = totalPriceCents - promoDiscountCents - inviteDiscountCents;

  // Store credit calculations
  const creditToUse = Math.min(creditBalance, postPromoAmountCents);
  const creditToUseDollars = creditToUse / 100;
  const remainingToPay = (postPromoAmountCents - creditToUse) / 100;
  const isCreditOnly = isAuthenticated && creditToUse > 0 && creditToUse >= postPromoAmountCents;
  const isPromoFree = postPromoAmountCents === 0 && promoDiscountCents + inviteDiscountCents > 0;

  const availableSpots = session.maxCapacity - session.currentBookings;
  const hasTemporaryParticipants =
    selectedChildren.some((child) => child.isTemporary || !child.profileId) ||
    selectedAdults.some((adult) => adult.isTemporary || !adult.profileId);

  // Pass mode: compute max selectable for the paying participant type
  const selectedPass = availablePasses.find((p) => p.id === selectedPassId);
  const passRemaining = selectedPass?.remainingSessions ?? 0;
  // In pass mode, limit the participant type that counts toward capacity
  const passChildMax =
    usePassMode && activity.childCountsTowardCapacity ? passRemaining : undefined;
  const passAdultMax =
    usePassMode && activity.adultCountsTowardCapacity ? passRemaining : undefined;

  return (
    <>
      {/* Profile Selectors */}
      {showChildProfileSelector && (
        <ProfileSelector
          type="child"
          profiles={childProfiles}
          selectedIds={selectedChildren
            .filter((c) => c.profileId)
            .map((c) => c.profileId as string)}
          onSelect={handleSelectChildProfiles}
          onClose={() => setShowChildProfileSelector(false)}
          maxSelectable={
            passChildMax !== undefined ? Math.max(0, passChildMax - childCount) : undefined
          }
        />
      )}

      {showAdultProfileSelector && (
        <ProfileSelector
          type="adult"
          profiles={adultProfiles}
          selectedIds={selectedAdults.filter((a) => a.profileId).map((a) => a.profileId as string)}
          onSelect={handleSelectAdultProfiles}
          onClose={() => setShowAdultProfileSelector(false)}
          maxSelectable={
            passAdultMax !== undefined ? Math.max(0, passAdultMax - adultCount) : undefined
          }
        />
      )}

      {/* Profile Creation Modals */}
      {showCreateChildModal && (
        <ChildProfileFormModal profile={null} onClose={handleCreateChildProfileClose} />
      )}

      {showCreateAdultModal && (
        <AdultProfileFormModal profile={null} onClose={handleCreateAdultProfileClose} />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container-custom max-w-4xl">
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="text-primary hover:underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t('booking:checkout.backButton')}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('booking:checkout.title')}
                </h1>

                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
                    ✓ {successMessage}
                  </div>
                )}

                <form
                  onSubmit={usePassMode ? handlePassBookingSubmit : handleSubmit}
                  className="space-y-8"
                >
                  {/* Login Prompt for Guest Users */}
                  {!isAuthenticated && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      {t('booking:checkout.contactInfo.loginPrompt')}{' '}
                      <button
                        type="button"
                        onClick={() => navigate('/login')}
                        className="underline hover:text-blue-900 font-medium"
                      >
                        {t('booking:checkout.contactInfo.signIn')}
                      </button>{' '}
                      {t('booking:checkout.contactInfo.loginMessage')}
                    </div>
                  )}

                  {/* Contact Information */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('booking:checkout.contactInfo.title')}
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('booking:checkout.contactInfo.email')}{' '}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          required
                          value={guestInfo.email}
                          onChange={(e) => {
                            setGuestInfo({ ...guestInfo, email: e.target.value.toLowerCase() });
                            emailValidationRef.current++;
                            setEmailWarning(null);
                            setEmailValidating(false);
                            // Invalidate any in-flight promo validate() and drop any applied code —
                            // the quota is keyed by email, so both stale responses and stale state must go.
                            promoValidationRef.current++;
                            setPromoValidating(false);
                            if (appliedPromo || appliedInvite) clearAllCodes();
                          }}
                          onBlur={(e) => {
                            validateGuestEmail(e.target.value);
                            void tryRememberedMemberCode(e.target.value.trim().toLowerCase());
                          }}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                            emailWarning ? 'border-amber-400' : 'border-gray-300'
                          }`}
                        />
                        {emailValidating && (
                          <p className="text-xs text-gray-400 mt-1">
                            {t('booking:checkout.contactInfo.emailChecking')}
                          </p>
                        )}
                        {emailWarning && (
                          <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-800">{emailWarning.message}</p>
                            {emailWarning.suggestion && (
                              <button
                                type="button"
                                onClick={() => {
                                  setGuestInfo({
                                    ...guestInfo,
                                    email: emailWarning.suggestion!,
                                  });
                                  setEmailWarning(null);
                                }}
                                className="text-sm text-primary font-medium mt-1 hover:underline"
                              >
                                {t('booking:checkout.contactInfo.emailUseSuggestion', {
                                  email: emailWarning.suggestion,
                                })}
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('booking:checkout.contactInfo.fullName')}{' '}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={guestInfo.name}
                          onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Use Pass Option */}
                  {isAuthenticated && availablePasses.length > 0 && (
                    <div className="p-4 bg-primary-pale border border-primary/20 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {t('pass:booking.usePassTitle')}
                        </h2>
                        {usePassMode && (
                          <button
                            type="button"
                            onClick={() => {
                              setUsePassMode(false);
                              setSelectedPassId(null);
                            }}
                            className="text-sm text-primary hover:underline font-medium"
                          >
                            {t('pass:booking.cancelPassMode')}
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {t('pass:booking.usePassDescription')}
                      </p>

                      {!usePassMode ? (
                        <button
                          type="button"
                          onClick={() => {
                            setUsePassMode(true);
                            setShowChildTempForm(false);
                            setShowAdultTempForm(false);
                            // When enabling pass mode, clear promo and new user promo codes
                            clearAllCodes();
                            if (availablePasses.length === 1) {
                              setSelectedPassId(availablePasses[0].id);
                            }
                          }}
                          className="w-full py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
                        >
                          {t('pass:booking.selectPass')}
                        </button>
                      ) : (
                        <div className="space-y-2">
                          {availablePasses.map((pass) => {
                            const passTitle = localize(
                              pass.pass.titleCN,
                              pass.pass.titleEN,
                              i18n.language
                            );
                            const expiresDate = new Date(pass.expiresAt).toLocaleDateString(
                              dateLocale(i18n.language),
                              {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                timeZone: DEFAULT_TIMEZONE,
                              }
                            );
                            return (
                              <label
                                key={pass.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                  selectedPassId === pass.id
                                    ? 'border-primary bg-white'
                                    : 'border-gray-200 bg-white/50 hover:border-primary/50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="selectedPass"
                                  value={pass.id}
                                  checked={selectedPassId === pass.id}
                                  onChange={() => setSelectedPassId(pass.id)}
                                  className="text-primary focus:ring-primary"
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{passTitle}</p>
                                  <p className="text-xs text-gray-500">
                                    {t('pass:booking.passInfo', {
                                      remaining: pass.remainingSessions,
                                      total: pass.pass.totalSessions,
                                      date: expiresDate,
                                    })}
                                  </p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {usePassMode && hasTemporaryParticipants && (
                        <p className="mt-3 text-sm text-red-700">
                          {t('pass:booking.profilesOnlyError')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Participants - Kids (hidden for adult-only activities) */}
                  {!isAdultOnly && (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {t('booking:checkout.childParticipants.title')}{' '}
                        <span className="text-red-500">
                          {t('booking:checkout.childParticipants.required')}
                        </span>
                      </h2>

                      {isAuthenticated ? (
                        // Authenticated user - profile-based selection
                        <div className="space-y-4">
                          {/* Profile load error */}
                          {profileLoadError && (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                              ⚠️ {profileLoadError}
                            </div>
                          )}

                          {profilesLoading ? (
                            <div className="text-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                              <p className="text-sm text-gray-600">
                                {t('booking:checkout.childParticipants.loadingProfiles')}
                              </p>
                            </div>
                          ) : childProfiles.length > 0 || selectedChildren.length > 0 ? (
                            // Has profiles or selected children
                            <>
                              {/* Selected children */}
                              {selectedChildren.map((child, idx) => (
                                <div key={idx}>
                                  {child.profile && !child.isTemporary ? (
                                    <SelectedProfileCard
                                      profile={child.profile}
                                      type="child"
                                      onRemove={() => handleRemoveChild(idx)}
                                    />
                                  ) : child.tempData ? (
                                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <div className="font-medium text-gray-900">
                                            {child.tempData.name}
                                          </div>
                                          <div className="text-sm text-gray-600">
                                            {t(
                                              'booking:checkout.childParticipants.temporaryParticipant'
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveChild(idx)}
                                          className="text-gray-400 hover:text-red-600 transition-colors"
                                        >
                                          <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M6 18L18 6M6 6l12 12"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              ))}

                              {usePassMode ? (
                                <button
                                  type="button"
                                  onClick={() => setShowChildProfileSelector(true)}
                                  disabled={
                                    passChildMax !== undefined && childCount >= passChildMax
                                  }
                                  className="w-full py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {passChildMax !== undefined && childCount >= passChildMax
                                    ? t('pass:booking.maxReached', { max: passChildMax })
                                    : t('booking:checkout.childParticipants.addFromProfiles')}
                                </button>
                              ) : showChildTempForm ? (
                                <TemporaryParticipantForm
                                  type="child"
                                  onAdd={handleAddTempChild}
                                  onCancel={() => setShowChildTempForm(false)}
                                />
                              ) : (
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setShowChildProfileSelector(true)}
                                    className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-primary hover:text-primary transition-colors"
                                  >
                                    {t('booking:checkout.childParticipants.addFromProfiles')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setShowChildTempForm(true)}
                                    className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-primary hover:text-primary transition-colors"
                                  >
                                    {t('booking:checkout.childParticipants.addTemporary')}
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            // No profiles - empty state
                            <>
                              {usePassMode ? (
                                <div className="p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 text-center">
                                  <p className="text-sm text-gray-600 mb-4">
                                    {t('pass:booking.profilesOnlyError')}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => setShowCreateChildModal(true)}
                                    className="py-2 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
                                  >
                                    {t('booking:components.emptyProfiles.createProfile', {
                                      type: t('booking:components.emptyProfiles.child'),
                                    })}
                                  </button>
                                </div>
                              ) : showChildTempForm ? (
                                <TemporaryParticipantForm
                                  type="child"
                                  onAdd={handleAddTempChild}
                                  onCancel={() => setShowChildTempForm(false)}
                                />
                              ) : (
                                <EmptyProfilesState
                                  type="child"
                                  onCreateProfile={() => setShowCreateChildModal(true)}
                                  onAddTemporary={() => setShowChildTempForm(true)}
                                />
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        // Guest user - manual input form
                        <div className="space-y-4">
                          {kids.map((kid, index) => (
                            <div
                              key={index}
                              className="p-4 border border-gray-200 rounded-lg space-y-3"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-700">
                                  {t('booking:checkout.childParticipants.child')} {index + 1}
                                </span>
                                {(isAdultOnly || kids.length > 1) && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveKid(index)}
                                    className="text-red-600 hover:text-red-700 text-sm"
                                  >
                                    {t('booking:checkout.childParticipants.remove')}
                                  </button>
                                )}
                              </div>

                              {/* Child Name (English full name) */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('booking:checkout.childParticipants.fields.name')}{' '}
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  required
                                  value={kid.name}
                                  onChange={(e) => handleKidChange(index, 'name', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                  placeholder={t(
                                    'booking:checkout.childParticipants.fields.namePlaceholder'
                                  )}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                  {t('booking:checkout.childParticipants.fields.nameHint')}
                                </p>
                              </div>

                              {/* Preferred Name (Nickname) */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('booking:checkout.childParticipants.fields.preferredName')}{' '}
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  required
                                  value={kid.preferredName}
                                  onChange={(e) =>
                                    handleKidChange(index, 'preferredName', e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                  placeholder={t(
                                    'booking:checkout.childParticipants.fields.preferredNamePlaceholder'
                                  )}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                  {t('booking:checkout.childParticipants.fields.preferredNameHint')}
                                </p>
                              </div>

                              {/* Birth Year & Month */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('booking:checkout.childParticipants.fields.birthDate')}{' '}
                                  <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                  <select
                                    required
                                    value={kid.birthYear}
                                    onChange={(e) =>
                                      handleKidChange(index, 'birthYear', e.target.value)
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                  >
                                    <option value="">
                                      {t('booking:checkout.childParticipants.fields.selectYear')}
                                    </option>
                                    {Array.from(
                                      { length: 18 },
                                      (_, i) => new Date().getFullYear() - i
                                    ).map((year) => (
                                      <option key={year} value={year}>
                                        {year}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    required
                                    value={kid.birthMonth}
                                    onChange={(e) =>
                                      handleKidChange(index, 'birthMonth', e.target.value)
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                  >
                                    <option value="">
                                      {t('booking:checkout.childParticipants.fields.selectMonth')}
                                    </option>
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                                      <option key={month} value={month}>
                                        {month}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* Special Needs */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('booking:checkout.childParticipants.fields.specialNeeds')}
                                </label>
                                <textarea
                                  value={kid.notes}
                                  onChange={(e) => handleKidChange(index, 'notes', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                  rows={3}
                                  placeholder={t(
                                    'booking:checkout.childParticipants.fields.specialNeedsPlaceholder'
                                  )}
                                />
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={handleAddKid}
                            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary hover:text-primary transition-colors"
                          >
                            {t('booking:checkout.childParticipants.addAnother')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Participants - Adults */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                      {isAdultOnly
                        ? t('booking:checkout.adultParticipants.participantTitle')
                        : t('booking:checkout.adultParticipants.title')}{' '}
                      {activity.adultRequired && <span className="text-red-500">*</span>}
                    </h2>
                    {!isAdultOnly && (
                      <p className="text-sm text-gray-600 mb-4">
                        {!activity.adultRequired
                          ? t('booking:checkout.adultParticipants.optionalNote')
                          : activity.adultPrice > 0
                            ? t('booking:checkout.adultParticipants.guardianNotePaid')
                            : t('booking:checkout.adultParticipants.guardianNote')}
                      </p>
                    )}

                    {isAuthenticated ? (
                      // Authenticated user - profile-based selection
                      <div className="space-y-4">
                        {profilesLoading ? (
                          <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                            <p className="text-sm text-gray-600">
                              {t('booking:checkout.adultParticipants.loadingProfiles')}
                            </p>
                          </div>
                        ) : adultProfiles.length > 0 || selectedAdults.length > 0 ? (
                          // Has profiles or selected adults
                          <>
                            {/* Selected adults */}
                            {selectedAdults.map((adult, idx) => (
                              <div key={idx}>
                                {adult.profile && !adult.isTemporary ? (
                                  <SelectedProfileCard
                                    profile={adult.profile}
                                    type="adult"
                                    onRemove={() => handleRemoveAdult(idx)}
                                  />
                                ) : adult.tempData ? (
                                  <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-medium text-gray-900">
                                          {adult.tempData.name}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                          {t(
                                            'booking:checkout.adultParticipants.temporaryParticipant'
                                          )}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveAdult(idx)}
                                        className="text-gray-400 hover:text-red-600 transition-colors"
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            ))}

                            {usePassMode ? (
                              <button
                                type="button"
                                onClick={() => setShowAdultProfileSelector(true)}
                                disabled={passAdultMax !== undefined && adultCount >= passAdultMax}
                                className="w-full py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {passAdultMax !== undefined && adultCount >= passAdultMax
                                  ? t('pass:booking.maxReached', { max: passAdultMax })
                                  : t('booking:checkout.adultParticipants.addFromProfiles')}
                              </button>
                            ) : showAdultTempForm ? (
                              <TemporaryParticipantForm
                                type="adult"
                                onAdd={handleAddTempAdult}
                                onCancel={() => setShowAdultTempForm(false)}
                              />
                            ) : (
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setShowAdultProfileSelector(true)}
                                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-primary hover:text-primary transition-colors"
                                >
                                  {t('booking:checkout.adultParticipants.addFromProfiles')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setShowAdultTempForm(true)}
                                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-primary hover:text-primary transition-colors"
                                >
                                  {t('booking:checkout.adultParticipants.addTemporary')}
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          // No profiles - empty state
                          <>
                            {usePassMode ? (
                              <div className="p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 text-center">
                                <p className="text-sm text-gray-600 mb-4">
                                  {t('pass:booking.profilesOnlyError')}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setShowCreateAdultModal(true)}
                                  className="py-2 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
                                >
                                  {t('booking:components.emptyProfiles.createProfile', {
                                    type: t('booking:components.emptyProfiles.adult'),
                                  })}
                                </button>
                              </div>
                            ) : showAdultTempForm ? (
                              <TemporaryParticipantForm
                                type="adult"
                                onAdd={handleAddTempAdult}
                                onCancel={() => setShowAdultTempForm(false)}
                              />
                            ) : (
                              <EmptyProfilesState
                                type="adult"
                                onCreateProfile={() => setShowCreateAdultModal(true)}
                                onAddTemporary={() => setShowAdultTempForm(true)}
                              />
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      // Guest user - manual input form
                      <div className="space-y-4">
                        {adults.map((adult, index) => (
                          <div
                            key={index}
                            className="p-4 border border-gray-200 rounded-lg space-y-3"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-700">
                                {t('booking:checkout.adultParticipants.adult')} {index + 1}
                              </span>
                              {adults.length > (activity.adultRequired ? 1 : 0) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newAdults = adults.filter((_, i) => i !== index);
                                    setAdults(newAdults);
                                  }}
                                  className="text-red-600 hover:text-red-700 text-sm"
                                >
                                  {t('booking:checkout.adultParticipants.remove')}
                                </button>
                              )}
                            </div>

                            {/* Adult Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('booking:checkout.adultParticipants.fields.name')}{' '}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                required
                                value={adult.name}
                                onChange={(e) => handleAdultChange(index, 'name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                placeholder={t(
                                  'booking:checkout.adultParticipants.fields.namePlaceholder'
                                )}
                              />
                            </div>

                            {/* Phone */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('booking:checkout.adultParticipants.fields.phone')}{' '}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="tel"
                                required
                                value={adult.phone}
                                onChange={(e) =>
                                  handleAdultChange(
                                    index,
                                    'phone',
                                    e.target.value.replace(/[^\d+\-()（）#转 ]/g, '')
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                placeholder={t(
                                  'booking:checkout.adultParticipants.fields.phonePlaceholder'
                                )}
                              />
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={handleAddAdult}
                          className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary hover:text-primary transition-colors"
                        >
                          {t('booking:checkout.adultParticipants.addAnother')}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Consent Section - hidden if user already signed all consents */}
                  {!hasExistingConsent && (
                    <ConsentSection
                      photoVideoConsent={photoVideoConsent}
                      waiverAgreed={waiverAgreed}
                      releaseAgreed={releaseAgreed}
                      isAdultOnly={isAdultOnly}
                      onPhotoConsentChange={setPhotoVideoConsent}
                      onWaiverChange={setWaiverAgreed}
                      onReleaseChange={setReleaseAgreed}
                    />
                  )}

                  {/* Refund Policy Checkbox (hidden in pass mode) */}
                  {!usePassMode && (
                    <div className="pt-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={refundPolicyAgreed}
                          onChange={(e) => setRefundPolicyAgreed(e.target.checked)}
                          className="mt-0.5"
                        />
                        <span className="text-sm text-gray-700">
                          {t('booking:consent.refundPolicy.agree')}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowRefundPolicyModal(true);
                            }}
                            className="text-primary hover:underline font-medium"
                          >
                            {t('booking:consent.refundPolicy.link')}
                          </button>
                        </span>
                      </label>
                    </div>
                  )}

                  {showRefundPolicyModal && (
                    <RefundPolicyModal onClose={() => setShowRefundPolicyModal(false)} />
                  )}

                  {/* Submit Button */}
                  <div className="pt-4">
                    {usePassMode ? (
                      <button
                        type="submit"
                        disabled={
                          passBookingSubmitting ||
                          !selectedPassId ||
                          (!isAdultOnly && childCount === 0) ||
                          (activity.adultRequired && adultCount === 0) ||
                          !photoVideoConsent ||
                          !waiverAgreed ||
                          (!isAdultOnly && !releaseAgreed)
                        }
                        className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {passBookingSubmitting
                          ? t('pass:booking.processing')
                          : t('pass:booking.proceedWithPass')}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={
                          submitting ||
                          (!isAdultOnly && childCount === 0) ||
                          (activity.adultRequired && adultCount === 0) ||
                          !photoVideoConsent ||
                          !waiverAgreed ||
                          (!isAdultOnly && !releaseAgreed) ||
                          !refundPolicyAgreed
                        }
                        className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting
                          ? t('booking:checkout.submit.processing')
                          : isPromoFree
                            ? t('booking:checkout.submit.confirmFree', '确认预订（免费）')
                            : isCreditOnly
                              ? t('booking:storeCredit.payWithCredit')
                              : t('booking:checkout.submit.button', {
                                  price:
                                    remainingToPay > 0
                                      ? remainingToPay.toFixed(2)
                                      : totalPrice.toFixed(2),
                                })}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Booking Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('booking:checkout.summary.title')}
                </h2>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {getLocalizedTitle(activity, i18n.language)}
                    </h3>
                  </div>

                  <div className="pt-4">
                    <div className="text-sm text-gray-600 mb-1">
                      {t('booking:checkout.summary.dateTime')}
                    </div>
                    <div className="font-medium text-gray-900">
                      {new Date(session.startTime).toLocaleDateString(dateLocale(i18n.language), {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        timeZone: DEFAULT_TIMEZONE,
                      })}
                    </div>
                    <div className="text-sm text-gray-600">
                      {new Date(session.startTime).toLocaleTimeString(dateLocale(i18n.language), {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                        timeZone: DEFAULT_TIMEZONE,
                      })}{' '}
                      -{' '}
                      {new Date(session.endTime).toLocaleTimeString(dateLocale(i18n.language), {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                        timeZone: DEFAULT_TIMEZONE,
                      })}
                    </div>
                    {(() => {
                      const desc = localize(
                        session.descriptionCN,
                        session.descriptionEN,
                        i18n.language
                      );
                      return desc ? <p className="text-xs text-gray-500 mt-1">{desc}</p> : null;
                    })()}
                  </div>

                  <div className="pt-4">
                    <div className="text-sm text-gray-600 mb-2">
                      {t('booking:checkout.summary.participants')}
                    </div>
                    <div className="space-y-1 text-sm">
                      {!isAdultOnly && childCount > 0 && (
                        <div className="flex justify-between">
                          <span>
                            {childCount}{' '}
                            {childCount === 1
                              ? t('booking:checkout.summary.child')
                              : t('booking:checkout.summary.children')}
                          </span>
                          {usePassMode && activity.childCountsTowardCapacity ? (
                            <span className="text-green-600">
                              {t('booking:checkout.summary.free')}
                            </span>
                          ) : (
                            <span>${(effectiveChildPrice * childCount).toFixed(2)}</span>
                          )}
                        </div>
                      )}
                      {adultCount > 0 && (
                        <div className="flex justify-between">
                          <span>
                            {adultCount}{' '}
                            {adultCount === 1
                              ? t('booking:checkout.summary.adult')
                              : t('booking:checkout.summary.adults')}
                            {effectiveAdultPrice === 0 && !isAdultOnly && !usePassMode && (
                              <> ({t('booking:checkout.summary.guardian')})</>
                            )}
                          </span>
                          {usePassMode && activity.adultCountsTowardCapacity ? (
                            <span className="text-green-600">
                              {t('booking:checkout.summary.free')}
                            </span>
                          ) : effectiveAdultPrice > 0 ? (
                            <span>${(effectiveAdultPrice * adultCount).toFixed(2)}</span>
                          ) : (
                            <span className="text-green-600">
                              {t('booking:checkout.summary.free')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-4">
                    {usePassMode ? (
                      <div>
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-900">
                            {t('booking:checkout.summary.total')}
                          </span>
                          <span className="text-2xl font-bold text-green-600">
                            {t('booking:checkout.summary.free')}
                          </span>
                        </div>
                        {selectedPass && (
                          <p className="text-xs text-gray-500 mt-1">
                            {t('pass:booking.sessionsUsed', {
                              used: activity.childCountsTowardCapacity ? childCount : adultCount,
                              remaining: passRemaining,
                            })}
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-900">
                            {t('booking:checkout.summary.total')}
                          </span>
                          <span className="text-2xl font-bold text-primary">
                            ${totalPrice.toFixed(2)}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Promotion Code (available to guests + authenticated users) */}
                    {!usePassMode && totalPriceCents > 0 && (
                      <div className="mt-2">
                        {appliedPromo && (
                          <>
                            <div className="flex justify-between text-sm mt-1">
                              <span className="text-green-700">
                                {t('booking:promotionCode.discountLine', {
                                  code: appliedPromo.code,
                                })}
                              </span>
                              <span className="text-green-700 font-medium">
                                -${(promoDiscountCents / 100).toFixed(2)}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemovePromoCode}
                              className="text-xs text-gray-400 hover:text-gray-600 mt-0.5"
                            >
                              {t('booking:promotionCode.remove')}
                            </button>
                          </>
                        )}
                        {appliedInvite && (
                          <>
                            <div className="flex justify-between text-sm mt-1">
                              <span className="text-green-700">
                                {t('booking:promotionCode.inviteLine', {
                                  code: appliedInvite.code,
                                })}
                              </span>
                              <span className="text-green-700 font-medium">
                                -${(inviteDiscountCents / 100).toFixed(2)}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveInviteCode}
                              className="text-xs text-gray-400 hover:text-gray-600 mt-0.5"
                            >
                              {t('booking:promotionCode.remove')}
                            </button>
                          </>
                        )}
                        {!(appliedPromo && appliedInvite) && (
                          <div className={appliedPromo || appliedInvite ? 'mt-2' : undefined}>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                placeholder={t('booking:promotionCode.placeholder')}
                                className="flex-1 min-w-0 border border-gray-200 rounded px-2.5 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-primary"
                                maxLength={20}
                              />
                              <button
                                type="button"
                                onClick={handleApplyPromoCode}
                                disabled={!promoCode.trim() || promoValidating}
                                className="shrink-0 px-3 py-1.5 text-sm text-primary font-medium hover:text-primary/80 disabled:text-gray-300 disabled:cursor-not-allowed"
                              >
                                {promoValidating ? '...' : t('booking:promotionCode.apply')}
                              </button>
                            </div>
                            {promoError && (
                              <p className="text-xs text-red-500 mt-1">{promoError}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Store Credit deduction (hidden in pass mode) */}
                    {!usePassMode &&
                      isAuthenticated &&
                      creditBalance > 0 &&
                      totalPriceCents > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-brown">{t('booking:storeCredit.applied')}</span>
                            <span className="text-brown-dark font-medium">
                              -${creditToUseDollars.toFixed(2)}
                            </span>
                          </div>
                          {remainingToPay > 0 && (
                            <div className="flex justify-between text-sm font-semibold">
                              <span className="text-gray-900">
                                {t('booking:storeCredit.remaining')}
                              </span>
                              <span className="text-gray-900">${remainingToPay.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      )}
                  </div>

                  <div className="pt-4 text-xs text-gray-500">
                    <div className="mb-2">
                      <strong>{t('booking:checkout.summary.availableSpots')}:</strong>{' '}
                      {availableSpots} / {session.maxCapacity}
                    </div>
                    <div>
                      <strong>{t('booking:checkout.summary.ageRange')}:</strong>{' '}
                      {activity.ageMax >= 99
                        ? `${activity.ageMin}+`
                        : `${activity.ageMin}-${activity.ageMax}`}{' '}
                      {t('booking:checkout.summary.years')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
